import { useEffect, useRef, useState, type ReactNode } from "react";
import { Download, Send } from "lucide-react";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { isEmail } from "@/utils/validation";
import { saveSvgAsPng } from "@/utils/download";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { toastApiError } from "@/lib/toast";

export default function ShareLinkCard({
  link,
  formName,
  qrFilename,
  onSend,
  emailHint,
  defaultEmail = "",
}: {
  link: string;
  formName: string;
  qrFilename: string;
  onSend: (to: string, toName: string | null) => Promise<string>;
  emailHint?: ReactNode;
  defaultEmail?: string;
}) {
  const [sendTo, setSendTo] = useState(defaultEmail);
  const [sendToName, setSendToName] = useState("");
  const [sending, setSending] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSendTo(defaultEmail);
  }, [defaultEmail]);

  function downloadQr() {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    saveSvgAsPng(svg, qrFilename).catch((e) => {
      toastApiError(e, "Couldn't download the QR code.");
    });
  }

  const to = sendTo.trim();
  const emailInvalid = to !== "" && !isEmail(to);
  const canSend = isEmail(to);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (!canSend) {
      toast.error("Enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      const sentTo = await onSend(to, sendToName.trim() || null);
      toast.success(`Sent to ${sentTo}.`);
      setSendTo(defaultEmail);
      setSendToName("");
    } catch (err) {
      toastApiError(err, "Couldn't send the email.");
    } finally {
      setSending(false);
    }
  }

  return (
    <Card>
      <CardHeader className="border-b py-3">
        <CardTitle className="text-base font-semibold text-slate-900">Share Link</CardTitle>
        <CardDescription>Send the link to a prospect by QR or email.</CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel className="flex items-start gap-4">
            <div ref={qrRef} className="bg-white p-5 border border-slate-200 rounded shrink-0">
              <QRCode value={link} size={112} />
            </div>
            <div className="space-y-2">
              <PanelLabel>QR code</PanelLabel>
              <p className="text-sm text-slate-600">
                Prospect can scan this QR code to open the {formName}.
              </p>
              <Button type="button" variant="outline" size="sm" onClick={downloadQr}>
                <Download />
                Download QR
              </Button>
            </div>
          </Panel>

          <Panel>
            <form onSubmit={send} className="space-y-3">
              <PanelLabel>Send by email</PanelLabel>
              <p className="text-sm text-slate-600">
                We'll email the prospect this link via SMTP configured under
                Settings → Notifications.{emailHint ? <> {emailHint}</> : null}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="sendToName">Recipient name</Label>
                  <Input
                    id="sendToName"
                    type="text"
                    value={sendToName}
                    onChange={(e) => setSendToName(e.target.value)}
                    placeholder="Jane Doe"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="sendTo">
                    Recipient email
                    <span className="text-destructive"> *</span>
                  </Label>
                  <Input
                    id="sendTo"
                    type="email"
                    value={sendTo}
                    onChange={(e) => setSendTo(e.target.value)}
                    placeholder="prospect@example.com"
                    aria-invalid={emailInvalid || undefined}
                  />
                </div>
              </div>
              {emailInvalid && (
                <p className="text-sm text-destructive">Enter a valid email address.</p>
              )}
              <Button type="submit" disabled={sending || !canSend}>
                <Send />
                {sending ? "Sending…" : "Send link"}
              </Button>
            </form>
          </Panel>
        </div>
      </CardContent>
    </Card>
  );
}

function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-lg border border-slate-200 bg-slate-50/60 p-4", className)}>
      {children}
    </div>
  );
}

function PanelLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </p>
  );
}
